import { formatDate } from "helper/dateFormat.js";
import { formatPhoneNumber } from "react-phone-number-input";
import { numberFormat } from "helper/numberFormat";

const findAddress = (row, flag) => row?.account?.addresses?.find((a) => a?.[flag] === true);

const findAddressField = (row, flag, field) => findAddress(row, flag)?.[field] ?? " - ";

const setAdultValue = (row, column) => {
  const raw = row?.account?.[column?.fieldName];
  if (!raw) return "-";
  return !isNaN(new Date(raw))
    ? formatDate({ date: raw, formatType: "defaultWithUTC" })
    : raw;
};

// Given a raw member/contact row and a fieldsOrder column definition
// ({ fieldName, displayName }), resolve the display value for that cell.
// Ported verbatim from the print component's per-field ternary chain
// (the most complete, bug-fixed version as of this refactor) — do not
// change any condition or output here without also verifying print,
// the on-screen results table, and any other consumer still agree.
export function resolveFieldValue(row, column) {
  if (column?.fieldName === "account.tags" || column?.fieldName === "tags") {
    const tags =
      row?.[column?.fieldName] ??
      (column?.fieldName === "tags" ? row?.tags : row?.account?.tags);
    return tags
      ?.slice()
      ?.sort((a, b) => a?.toLowerCase().localeCompare(b?.toLowerCase()))
      .join(" , ");
  }
  if (column?.fieldName === "date_of_birth") {
    return row?.[column?.fieldName]
      ? formatDate({ date: row?.[column?.fieldName], formatType: "defaultWithUTC" })
      : " - ";
  }
  if (column?.fieldName === "last_active") {
    return row?.[column?.fieldName]
      ? formatDate({ date: row?.[column?.fieldName], formatType: "defaultWithUTC" })
      : " - ";
  }
  if (column?.fieldName === "next_birthday") {
    return row?.[column?.fieldName]
      ? formatDate({ date: row?.[column?.fieldName], formatType: "defaultWithUTC" })
      : " - ";
  }
  if (column?.fieldName === "home_phone") {
    return row?.[column?.fieldName] ? formatPhoneNumber(row?.[column?.fieldName]) : " - ";
  }
  if (column?.fieldName === "mobile_number") {
    return row?.[column?.fieldName] ? formatPhoneNumber(row?.[column?.fieldName]) : " - ";
  }
  if (column?.fieldName === "emergency_contact_number") {
    return row?.[column?.fieldName] ? formatPhoneNumber(row?.[column?.fieldName]) : " - ";
  }
  if (column?.fieldName === "account.admin_contact") {
    const val = row?.["account.admin_contact"] ?? row?.account?.admin_contact;
    return val ? formatPhoneNumber(val) : " - ";
  }
  if (column?.displayName === "Anniversary") {
    return row?.[column?.fieldName]
      ? formatDate({ date: row?.[column?.fieldName], formatType: "defaultWithUTC" })
      : " - ";
  }
  if (column?.displayName === "Bar/Bat Mitzvah") {
    return row?.[column?.fieldName]
      ? formatDate({ date: row?.[column?.fieldName], formatType: "defaultWithUTC" })
      : " - ";
  }
  if (column?.fieldName === "Adult 1 Mobile") {
    const val = row?.["account.adult1Mobile"] ?? row?.account?.["Adult 1 Mobile"];
    return val ? formatPhoneNumber(val) : " - ";
  }
  if (column?.fieldName === "Adult 2 Mobile") {
    const val = row?.["account.adult2Mobile"] ?? row?.account?.["Adult 2 Mobile"];
    return val ? formatPhoneNumber(val) : " - ";
  }
  if (column?.fieldName === "Home 1" || column?.displayName === "Home 1") {
    const val = row?.["account.home1"] ?? row?.account?.["Home 1"];
    return val ? formatPhoneNumber(val) : " - ";
  }
  if (column?.fieldName === "Home 2" || column?.displayName === "Home 2") {
    const val = row?.["account.home2"] ?? row?.account?.["Home 2"];
    return val ? formatPhoneNumber(val) : " - ";
  }
  if (column?.fieldName === "next_anniversary") {
    return row?.[column?.fieldName]
      ? formatDate({ date: row?.[column?.fieldName], formatType: "defaultWithUTC" })
      : " - ";
  }
  if (column?.fieldName === "anniversary_date") {
    return row?.[column?.fieldName]
      ? formatDate({ date: row?.[column?.fieldName], formatType: "defaultWithUTC" })
      : " - ";
  }
  if (column?.fieldName === "metva_date") {
    return row?.[column?.fieldName]
      ? formatDate({ date: row?.[column?.fieldName], formatType: "defaultWithUTC" })
      : " - ";
  }
  if (column?.fieldName === "account.join_date") {
    const val = row?.[column?.fieldName] ?? row?.account?.join_date;
    return val ? formatDate({ date: val, formatType: "defaultWithUTC" }) : " - ";
  }
  if (column?.fieldName === "account.resign_date") {
    const val = row?.account?.resign_date ?? row?.[column?.fieldName];
    return val ? formatDate({ date: val, formatType: "defaultWithUTC" }) : " - ";
  }
  if (column?.fieldName === "unsubscribedEmail") {
    return row?.[column?.fieldName] ? "Yes" : "No";
  }
  if (column?.fieldName === "unsubscribedDateEmail") {
    return row?.[column?.fieldName]
      ? formatDate({ date: row?.[column?.fieldName], formatType: "defaultWithUTC" })
      : " - ";
  }
  if (column?.fieldName === "unsubscribedText") {
    return row?.[column?.fieldName] ? "Yes" : "No";
  }
  if (column?.fieldName === "unsubscribedDateText") {
    return row?.[column?.fieldName]
      ? formatDate({ date: row?.[column?.fieldName], formatType: "defaultWithUTC" })
      : " - ";
  }
  if (column?.fieldName === "mark_as_deceased") {
    return row?.[column?.fieldName] ? "Yes" : "No";
  }
  if (column?.fieldName === "hebrewDateOfBirth.jewishDateStr" || column?.displayName === "Hebrew date") {
    const val = row?.[column?.fieldName] ?? row?.hebrewDateOfBirth?.jewishDateStr;
    return val ? val : " - ";
  }
  if (column?.fieldName === "hebrew_name" || column?.displayName === "Hebrew Name") {
    const val = row?.[column?.fieldName] ?? row?.hebrew_name;
    return val ? val : " - ";
  }
  if (column?.fieldName === "account.wallet.balance") {
    return numberFormat({ number: row?.account?.wallet?.balance ?? 0, decimal: 2, style: "currency" });
  }
  if (column?.fieldName?.includes("Adult")) {
    return setAdultValue(row, column);
  }
  if (column?.displayName === "Hide on member directory") {
    return row?.account?.hideDirectory ? "Yes" : "No";
  }
  if (column?.displayName === "Hide email address on member directory") {
    return row?.account?.hideEmail ? "Yes" : "No";
  }
  if (column?.displayName === "Hide mobile number on member directory") {
    return row?.account?.hideMobile ? "Yes" : "No";
  }
  if (column?.displayName === "Hide home number on member directory") {
    return row?.account?.hideHomeNumber ? "Yes" : "No";
  }
  if (column?.displayName === "Hide address on member directory") {
    return row?.account?.hideAddress ? "Yes" : "No";
  }
  if (column?.displayName === "Archived account") {
    return row?.account?.archived ? "Yes" : "No";
  }
  if (column?.displayName === "Archived contact") {
    return row?.archived ? "Yes" : "No";
  }
  if (column?.displayName === "Contact id") {
    return row?._id || row?.id || " - ";
  }
  if (column?.displayName === "Active City") return findAddressField(row, "active", "city");
  if (column?.displayName === "Active State") return findAddressField(row, "active", "state");
  if (column?.displayName === "Active Country") return findAddressField(row, "active", "country");
  if (column?.displayName === "Active Zip code") return findAddressField(row, "active", "zip_code");
  if (column?.displayName === "Active Neighborhood") return findAddressField(row, "active", "neighborhood");
  if (column?.displayName === "Active Address Line 2") {
    return (
      row?.["account.addresses.active_addressLine2"] ??
      findAddress(row, "active")?.addressLine2 ??
      " - "
    );
  }
  if (column?.displayName === "Active Address") {
    return (
      findAddress(row, "active")?.location?.formattedAddress?.split(",")[0] ?? " - "
    );
  }
  if (column?.displayName === "Billing City") return findAddressField(row, "billing", "city");
  if (column?.displayName === "Billing State") return findAddressField(row, "billing", "state");
  if (column?.displayName === "Billing Country") return findAddressField(row, "billing", "country");
  if (column?.displayName === "Billing Zip code") return findAddressField(row, "billing", "zip_code");
  if (column?.displayName === "Billing Neighborhood") return findAddressField(row, "billing", "neighborhood");
  if (column?.displayName === "Billing Address Line 2") {
    return (
      row?.["account.addresses.billing_addressLine2"] ??
      findAddress(row, "billing")?.addressLine2 ??
      " - "
    );
  }
  if (column?.displayName === "Billing Address") {
    return (
      row?.["account.addresses.billing_address"] ??
      findAddress(row, "billing")?.location?.formattedAddress?.split(",")[0] ??
      " - "
    );
  }
  if (column?.displayName === "Account name") {
    return row?.account?.family_name ?? " - ";
  }
  if (column?.displayName === "Account type") {
    return row?.account?.payment?.account_type ?? " - ";
  }
  if (column?.displayName === "Category") {
    return row?.account?.payment?.account_sub_type?.title ?? " - ";
  }
  if (column?.displayName === "Old account ID") {
    return row?.account?.old_account_id ?? " - ";
  }
  if (column?.displayName === "Account ID") {
    return row?.account?.account_id ?? " - ";
  }
  if (column?.displayName === "Billing type") {
    return row?.account?.payment?.billing_type?.title ?? " - ";
  }
  if (column?.displayName === "Relationship") {
    return Array.isArray(row?.relationship)
      ? row.relationship.map((r) => r?.label || r?.title || r?.value || r).filter(Boolean).join(", ")
      : row?.relationship ?? " - ";
  }
  if (column?.displayName === "Number of family members") {
    return row?.account?.numberOfFamilyMembers ?? " - ";
  }
  if (column?.displayName === "Mailing label name") {
    return row?.account?.mailLabelName ?? " - ";
  }
  if (column?.displayName === "Family admin name") {
    return row?.account?.admin_name ?? " - ";
  }
  if (column?.displayName === "Family admin contact") {
    return row?.account?.admin_contact ? formatPhoneNumber(row?.account?.admin_contact) : " - ";
  }
  if (column?.displayName === "Emergency Contact Number") {
    return row?.emergency_contact_number ? formatPhoneNumber(row?.emergency_contact_number) : " - ";
  }
  if (column?.displayName === "Email Unsubscribe") {
    return row?.unsubscribedEmail ? "Yes" : "No";
  }
  if (column?.displayName === "Email Unsubscribe Date") {
    return row?.unsubscribedDateEmail
      ? formatDate({ date: row?.unsubscribedDateEmail, formatType: "defaultWithUTC" })
      : " - ";
  }
  if (column?.displayName === "Text unsubscribe") {
    return row?.unsubscribedText ? "Yes" : "No";
  }
  if (column?.displayName === "Text unsubscribe Date") {
    return row?.unsubscribedDateText
      ? formatDate({ date: row?.unsubscribedDateText, formatType: "defaultWithUTC" })
      : " - ";
  }
  if (column?.displayName === "Last login") {
    return row?.last_active
      ? formatDate({ date: row?.last_active, formatType: "defaultWithUTC" })
      : " - ";
  }
  if (column?.displayName === "Marked as decease") {
    return row?.mark_as_deceased ? "Yes" : "No";
  }
  return (
    row?.[column?.fieldName] ??
    (column?.fieldName?.includes(".")
      ? column.fieldName.split(".").reduce((obj, key) => obj?.[key], row) ?? " - "
      : " - ")
  );
}

// Export-flavored wrapper for consumers (e.g. the segment table's row
// print/download) that expect Table.jsx's existing export conventions:
// tags left in their stored (unsorted) order, and missing values as
// `undefined` (so the column is omitted) rather than the literal " - "
// placeholder used by the stepper-3 print/table UI.
export function resolveFieldValueForExport(row, column) {
  if (column?.fieldName === "account.tags" || column?.fieldName === "tags") {
    const tags = column?.fieldName === "tags" ? row?.tags : row?.account?.tags;
    return Array.isArray(tags) ? tags.join(" , ") : tags;
  }
  const value = resolveFieldValue(row, column);
  return value === " - " || value === "-" || value === undefined || value === null
    ? undefined
    : value;
}
